import React from 'react';
import propTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import TextField from '@material-ui/core/TextField';
import Divider from '@material-ui/core/Divider';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import CloseIcon from '@material-ui/icons/Close';
import $api from 'http/index';
import IconSunny from 'assets/images/icon-sunny.svg';
import IconMoon from 'assets/images/icon-moon.svg';
import HICodes from 'components/HICodes/HICodes';
import IOSSwitch from 'components/IOSSwitch';
import styles from './styles';
import { isIOS, Option } from '../../../../../../components/Option';
import TimeInput from 'components/TimeInput';
import { useDirectoryName } from 'common/hooks/use-directory-name';
import { isAvailabilityEnabled } from 'common/permissions';
import {
  postHiCode,
  putHICode,
  deleteHICode
} from 'store/people/hiCodesOperations';

const UserDetails = ({
  classes,
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
  userInfo,
  openSnackbar,
  // isCreate,
  setFieldValue,
}) => {
  const dispatch = useDispatch();
  const { hiCodes, hiCodesLoading } = useSelector((state) => state.people.hiCodes);
  const locationSettings = useSelector(state => state.personalProfile?.organizationLocation?.settings)
  const isFilingCabinetOn = useSelector(
    state => !!state.personalProfile?.organization?.settings?.businessFilingCabinet
  )
  const directoryName = useDirectoryName();

  const handleEmploymentStatusChange = (e) => {
    return handleChange({
      target: {
        name: 'timeOfDay',
        value: +e.target.value ? 'Day' : 'Inactive',
      },
    });
  };

  const handlePhoneChange = (e) => {
    handleChange(e);
    if (!e.target.value.trim()) setFieldValue('businessDirectory.phone', false);
  };

  const handleIndividualTimeChange = ({ hour, minute, amPm }) => {
    setFieldValue('individualHours', hour);
    setFieldValue('individuaMinutes', minute);
    setFieldValue('individualAmPm', amPm);
  };

  const handleClearIndividualHours = (e) => {
    e.preventDefault();
    handleChange({ target: { name: 'individualHours', value: null } });
    handleChange({ target: { name: 'individuaMinutes', value: null } });
    handleChange({ target: { name: 'individualAmPm', value: null } });
  };

  const handleResetPassword = async () => {
    const options = {
      headers: {
        'X-Auth-Token': localStorage.getItem('authorization-token'),
      },
    };

    try {
      await $api.post(
        `${process.env.REACT_APP_BASE_URL}/users/${values.userId}/reset-password`,
        {},
        options
      );
      openSnackbar('success', 'Link to reset password was successfully sent');
    } catch (error) {
      openSnackbar('error', error.response.data.message);
    }
  };

  const createHICodeAction = (input) => dispatch(postHiCode(input));
  const updateHICodeAction = (input) => dispatch(putHICode(input));
  const deleteHICodeAction = (input) => dispatch(deleteHICode(input));

  return (
    <>
      <Grid container spacing={2} alignItems={'center'}>
        <Grid item xs={12} md={6} lg={4}>
          <label htmlFor="name">
            <Typography variant="body1" color="textSecondary">
              Username<span className={classes.requiredSign}>*</span>:
            </Typography>
            <TextField
              type="text"
              id="name"
              value={values.name}
              onChange={handleChange}
              error={Boolean(errors.name) && Boolean(touched.name)}
              onBlur={handleBlur}
              className={classes.textField}
              variant="outlined"
              inputProps={{ 'aria-label': 'Username' }}
              fullWidth
            />
            {errors.name && touched.name ? (
              <div className={classes.error}>{errors.name}</div>
            ) : null}
          </label>
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <label htmlFor="fullName">
            <Typography variant="body1" color="textSecondary">
              Full Name<span className={classes.requiredSign}>*</span>:
            </Typography>
            <TextField
              type="text"
              id="fullName"
              value={values.fullName}
              onChange={handleChange}
              error={Boolean(errors.fullName) && Boolean(touched.fullName)}
              onBlur={handleBlur}
              className={classes.textField}
              variant="outlined"
              inputProps={{ 'aria-label': 'Full Name' }}
              fullWidth
            />
            {errors.fullName && touched.fullName ? (
              <div className={classes.error}>{errors.fullName}</div>
            ) : null}
          </label>
        </Grid>
        {userInfo && (
          <Grid item xs={12} md={6} lg={4}>
            <Typography
              variant="body1"
              color="primary"
              style={{ marginTop: '15px', cursor: 'pointer' }}
              onClick={handleResetPassword}
            >
              Reset password
            </Typography>
          </Grid>
        )}

        <Grid item xs={12} md={6} lg={4}>
          <label htmlFor="email">
            <Typography variant="body1" color="textSecondary">
              Email<span className={classes.requiredSign}>*</span>:
            </Typography>
            <TextField
              type="text"
              id="email"
              autoComplete="false"
              value={values.email}
              error={Boolean(errors.email) && Boolean(touched.email)}
              onBlur={handleBlur}
              onChange={handleChange}
              className={classes.textField}
              variant="outlined"
              inputProps={{ 'aria-label': 'Email' }}
              fullWidth
              // disabled={!isCreate}
            />
            {errors.email && touched.email ? (
              <div className={classes.error}>{errors.email}</div>
            ) : null}
          </label>
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <label htmlFor="phoneNumber">
            <Typography variant="body1" color="textSecondary">
              Phone Number:
            </Typography>
            <TextField
              type="text"
              id="phoneNumber"
              value={values.phoneNumber}
              error={Boolean(errors.phoneNumber) && Boolean(touched.phoneNumber)}
              onBlur={handleBlur}
              onChange={handlePhoneChange}
              className={classes.textField}
              variant="outlined"
              inputProps={{ 'aria-label': 'Phone number' }}
              fullWidth
              // disabled={!isCreate}
            />
            {errors.phoneNumber && touched.phoneNumber ? (
              <div className={classes.error}>{errors.phoneNumber}</div>
            ) : null}
          </label>
        </Grid>
      </Grid>
      <Divider className={classes.marginVertical} />
      <Grid container spacing={2}>
        <Grid item xs={12} md={6} lg={4}>
          <label htmlFor="employeeNum">
            <Typography variant="body1" color="textSecondary">
              Employee Number<span className={classes.requiredSign}>*</span>:
            </Typography>
            <TextField
              type="text"
              id="employeeNum"
              value={values.employeeNum}
              onChange={handleChange}
              error={Boolean(errors.employeeNum) && Boolean(touched.employeeNum)}
              onBlur={handleBlur}
              className={classes.textField}
              variant="outlined"
              inputProps={{ 'aria-label': 'Employee number' }}
              fullWidth
            />
            {errors.employeeNum && touched.employeeNum ? (
              <div className={classes.error}>{errors.employeeNum}</div>
            ) : null}
          </label>
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <label htmlFor="HICode">
            <Typography variant="body1" color="textSecondary">
              Health Insurance Code<span className={classes.requiredSign}>*</span>:
            </Typography>
            <HICodes
              value={values.HICode || ''}
              changeValue={(code) => setFieldValue('HICode', code)}
              handleBlur={handleBlur}
              errors={errors}
              touched={touched}
              openSnackbar={openSnackbar}
              options={hiCodes}
              loading={hiCodesLoading}
              createAction={createHICodeAction}
              editAction={updateHICodeAction}
              deleteAction={deleteHICodeAction}
            />
          </label>
          {errors.HICode && touched.HICode ? (
            <div className={classes.error}>{errors.HICode}</div>
          ) : null}
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <label htmlFor="timeOfDay">
            <Typography variant="body1" color="textSecondary">
              Shift:
            </Typography>
            <TextField
              select
              id="timeOfDay"
              name="timeOfDay"
              value={values.timeOfDay === 'Inactive' ? 'Day' : values.timeOfDay}
              onChange={handleChange}
              className={classes.textField}
              variant="outlined"
              inputProps={{ 'aria-label': 'Shift' }}
              fullWidth
              SelectProps={{
                native: isIOS,
              }}
              disabled={values.timeOfDay === 'Inactive'}
            >
              <Option value="Day">
                {isIOS ? '🌤 ' : <img src={IconSunny} alt="sunny" className={classes.shiftImage} />}
                Day
              </Option>
              <Option value="Night">
                {isIOS ? '🌙 ' : <img src={IconMoon} alt="moon" className={classes.shiftImage} />}
                Night
              </Option>
            </TextField>
          </label>
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <label htmlFor="weekDays">
            <Typography variant="body1" color="textSecondary">
              Days:
            </Typography>
            <TextField
              select
              id="weekDays"
              name="weekDays"
              value={values.weekDays}
              onChange={handleChange}
              className={classes.textField}
              variant="outlined"
              inputProps={{ 'aria-label': 'Days' }}
              SelectProps={{
                native: isIOS,
              }}
              fullWidth
            >
              <Option value="Mon-Fri">Mon &mdash; Fri</Option>
              <Option value="Sun-Thu">Sun &mdash; Thu</Option>
              <Option value="Tue-Sat">Tue &mdash; Sat</Option>
            </TextField>
          </label>
        </Grid>
        {isAvailabilityEnabled(locationSettings) && (
          <Grid item xs={12} md={6} lg={4}>
            <label htmlFor="allowedTime">
              <Typography variant="body1" color="textSecondary">
                PTO Hours Per Year:
              </Typography>
              <TextField
                type="text"
                id="allowedTime"
                value={values.allowedTime}
                onChange={handleChange}
                error={Boolean(errors.allowedTime) && Boolean(touched.allowedTime)}
                onBlur={handleBlur}
                className={classes.textField}
                variant="outlined"
                inputProps={{ 'aria-label': 'PTO hours per year' }}
                fullWidth
              />
              {errors.allowedTime && touched.allowedTime ? (
                <div className={classes.error}>{errors.allowedTime}</div>
              ) : null}
            </label>
          </Grid>
        )}
        {locationSettings?.timeclock && (
          <Grid item xs={12} md={6} lg={4}>
            <label htmlFor={'individualHours'} className={classes.label}>
              <Typography variant="body1" color="textSecondary">
                Individual Start Time:
              </Typography>
              <div className={classes.wrapper}>
                <TimeInput
                  value={{
                    hour: values.individualHours,
                    minute: values.individuaMinutes,
                    amPm: values.individualAmPm,
                  }}
                  onChange={handleIndividualTimeChange}
                  className={classes.time}
                />
                <CloseIcon className={classes.clearSelect} onClick={handleClearIndividualHours} />
              </div>
            </label>
          </Grid>
        )}
        <Grid item xs={12} md={6} lg={4}>
          <label htmlFor="employmentStatus">
            <Typography variant="body1" color="textSecondary">
              Employment Status:
            </Typography>
            <TextField
              select
              type="text"
              id="employmentStatus"
              value={Number(values.timeOfDay !== 'Inactive')}
              onChange={handleEmploymentStatusChange}
              className={classes.textField}
              variant="outlined"
              inputProps={{ 'aria-label': 'Employment Status' }}
              SelectProps={{
                native: isIOS,
              }}
              fullWidth
            >
              <Option value={1}>Active</Option>
              <Option value={0}>Inactive</Option>
            </TextField>
          </label>
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <label htmlFor="wageFormat">
            <Typography variant="body1" color="textSecondary">
              Wage Format:
            </Typography>
            <TextField
              select
              type="text"
              id="wageFormat"
              name="wageFormat"
              value={values.wageFormat}
              onChange={handleChange}
              className={classes.textField}
              variant="outlined"
              inputProps={{ 'aria-label': 'Wage Format' }}
              SelectProps={{
                native: isIOS,
              }}
              fullWidth
            >
              <Option value={'hourly'}>Hourly</Option>
              <Option value={'salary'}>Salary</Option>
            </TextField>
          </label>
        </Grid>
      </Grid>

      {!!values?.emergencyContacts?.length && (
        <>
          <Divider xs={9} className={'settings-devider'} />
          <Typography variant="h5" style={{ marginBottom: '25px' }}>
            Emergency contacts
          </Typography>
          {values.emergencyContacts.map((el, i) => (
            <div key={el.id} className={'emergency-block'}>
              <Typography variant="h6">{el.relationship}</Typography>
              <Grid container spacing={2} alignItems={'center'}>
                <Grid item xs={12} md={6} lg={4}>
                  <label htmlFor={`emergencyContacts[${i}].name`}>
                    <Typography variant="body1" color="textSecondary">
                      Name:
                    </Typography>
                    <TextField
                      type="text"
                      id={`emergencyContacts[${i}].name`}
                      value={values.emergencyContacts[i].name}
                      disabled
                      onBlur={handleBlur}
                      className={classes.textField}
                      variant="outlined"
                      inputProps={{ 'aria-label': 'Name' }}
                      fullWidth
                    />
                  </label>
                </Grid>

                <Grid item xs={12} md={6} lg={4}>
                  <label htmlFor={`values.emergencyContacts[${i}].phoneNumber`}>
                    <Typography variant="body1" color="textSecondary">
                      Phone number:
                    </Typography>
                    <TextField
                      type="text"
                      id={`values.emergencyContacts[${i}].phoneNumber`}
                      value={values.emergencyContacts[i].phoneNumber}
                      disabled
                      onBlur={handleBlur}
                      className={classes.textField}
                      variant="outlined"
                      inputProps={{ 'aria-label': 'Phone number' }}
                      fullWidth
                    />
                  </label>
                </Grid>

                <Grid item xs={12} md={6} lg={4}>
                  <label htmlFor={`values.emergencyContacts[${i}].address`}>
                    <Typography variant="body1" color="textSecondary">
                      Address:
                    </Typography>
                    <TextField
                      type="text"
                      disabled
                      id={`values.emergencyContacts[${i}].address`}
                      value={values.emergencyContacts[i].address}
                      onBlur={handleBlur}
                      className={classes.textField}
                      variant="outlined"
                      inputProps={{ 'aria-label': 'Address' }}
                      fullWidth
                    />
                  </label>
                </Grid>
              </Grid>
            </div>
          ))}
        </>
      )}

      {isFilingCabinetOn && (
        <>
          <Divider className={'settings-devider'} />
          <Typography variant="h5" style={{ marginBottom: '25px' }}>
            {directoryName}
          </Typography>
          <Grid container spacing={2} justifyContent="space-between">
            <Grid item xs={12} md={5} lg={5}>
              <FormControlLabel
                label="Phone:"
                labelPlacement="start"
                control={
                  <IOSSwitch
                    name="businessDirectory.phone"
                    checked={!!values.businessDirectory?.phone}
                    // an already-on record without a number must stay switchable off
                    disabled={!values.phoneNumber.trim() && !values.businessDirectory?.phone}
                    onChange={(e) => setFieldValue('businessDirectory.phone', e.target.checked)}
                  />
                }
              />
            </Grid>
            <Grid item xs={12} md={5} lg={5}>
              <FormControlLabel
                label="Email:"
                labelPlacement="start"
                control={
                  <IOSSwitch
                    name="businessDirectory.email"
                    checked={!!values.businessDirectory?.email}
                    onChange={(e) => setFieldValue('businessDirectory.email', e.target.checked)}
                  />
                }
              />
            </Grid>
          </Grid>
        </>
      )}
    </>
  );
};

UserDetails.propTypes = {
  classes: propTypes.object.isRequired,
  values: propTypes.object.isRequired,
  errors: propTypes.object.isRequired,
  touched: propTypes.object.isRequired,
  handleChange: propTypes.func.isRequired,
  handleBlur: propTypes.func.isRequired,
};

export default withStyles(styles)(UserDetails);
